import { Fragment } from "react";
import { useRouter } from "@/lib/navigation";
import { ArrowLeft, User as UserIcon, X } from "lucide-react";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
  Button,
} from "@myela/ui";
import { Truncate } from "@/components/common/Truncate";
import { IconTooltip } from "@/components/ui/icon-tooltip";
import { useAuth } from "@/hooks/useAuth";
import { useBreadcrumbs, useHeaderActions } from "./BreadcrumbContext";

// Slim content header: the breadcrumb, with the impersonation indicator pinned
// right. The menu, its bell and the phone's top bar live in PrimaryNav.
export function AppHeader() {
  const router = useRouter();
  const { impersonation, endImpersonation } = useAuth();
  const crumbs = useBreadcrumbs();
  const headerActions = useHeaderActions();
  // One level up: the crumb before the current page, when it's linked.
  const parent = crumbs.length >= 2 ? crumbs[crumbs.length - 2] : null;
  const backCrumb = parent && (parent.onClick || parent.href) ? parent : null;

  // List/index pages publish no breadcrumb, so no bar at all. Header actions
  // (e.g. Viewing as) keep it, aligned with the crumb row.
  const hasContent =
    crumbs.length > 0 || !!headerActions || impersonation.isImpersonating;
  if (!hasContent) return null;

  const impName = (() => {
    const u = impersonation.impersonatedUser;
    if (!u) return "";
    const name = `${u.firstName ?? ""} ${u.lastName ?? ""}`.trim();
    return name || u.email || "user";
  })();

  const handleEndImpersonation = async () => {
    await endImpersonation();
    window.location.reload();
  };

  return (
    // z-30: above scrolling page content, below overlays (sheets z-50, dialogs z-200).
    // Borderless — blends into the page; horizontal padding matches PageContainer
    // so the breadcrumb lines up with the content below it.
    <header className="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 bg-background px-4 text-foreground sm:px-6 lg:px-8">
      {backCrumb && (
        <button
          type="button"
          aria-label={typeof backCrumb.label === "string" ? `Back to ${backCrumb.label}` : "Back"}
          title={typeof backCrumb.label === "string" ? `Back to ${backCrumb.label}` : "Back"}
          onClick={() => {
            if (backCrumb.onClick) backCrumb.onClick();
            else if (backCrumb.href) router.push(backCrumb.href);
          }}
          className="inline-flex shrink-0 cursor-pointer items-center text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>
      )}

      {/* min-w-0 + flex-nowrap let the trail shrink instead of wrapping onto a
          second line the h-12 header has no room for; the last crumb absorbs it. */}
      <Breadcrumb className="min-w-0">
        <BreadcrumbList className="flex-nowrap">
          {crumbs.map((crumb, i) => {
            const isLast = i === crumbs.length - 1;
            const linked = !isLast && (crumb.href || crumb.onClick);
            return (
              <Fragment key={i}>
                <BreadcrumbItem className={isLast ? "min-w-0" : "shrink-0"}>
                  {linked ? (
                    <BreadcrumbLink
                      href={crumb.href ?? "#"}
                      onClick={(e) => {
                        e.preventDefault();
                        if (crumb.onClick) crumb.onClick();
                        else if (crumb.href) router.push(crumb.href);
                      }}
                      className="cursor-pointer"
                    >
                      {crumb.label}
                    </BreadcrumbLink>
                  ) : (
                    <BreadcrumbPage className="min-w-0">
                      <Truncate>{crumb.label}</Truncate>
                    </BreadcrumbPage>
                  )}
                </BreadcrumbItem>
                {!isLast && <BreadcrumbSeparator className="shrink-0" />}
              </Fragment>
            );
          })}
        </BreadcrumbList>
      </Breadcrumb>

      <div className="flex-1" />

      {headerActions && (
        <div className="flex shrink-0 items-center gap-2">{headerActions}</div>
      )}

      {impersonation.isImpersonating && impersonation.impersonatedUser && (
        <div
          className="flex items-center gap-1.5 rounded-full border border-amber-300 bg-amber-50 py-1 pl-2.5 pr-1 text-amber-800 dark:border-amber-500/40 dark:bg-amber-500/15 dark:text-amber-200"
          title="Read-only mode — actions are disabled while impersonating"
        >
          <UserIcon className="size-3.5 shrink-0" />
          <span className="max-w-[220px] truncate text-[12px] font-medium">
            Impersonating {impName}
          </span>
          <IconTooltip label="Exit impersonation">
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              onClick={handleEndImpersonation}
              aria-label="Exit impersonation"
              className="size-5 rounded-full text-amber-800 hover:bg-amber-100 hover:text-amber-900 dark:text-amber-200 dark:hover:bg-amber-500/20 dark:hover:text-amber-100"
            >
              <X className="size-3.5" />
            </Button>
          </IconTooltip>
        </div>
      )}
    </header>
  );
}
